import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import type { TenantTimePoll } from '@tark/shared/types';
import { CalendarPlus, Send, SquarePen } from 'lucide-react';
import { formatTimePollSlot } from '../../lib/timePolls';

type Props = {
  poll: TenantTimePoll;
  working: boolean;
  onEdit: () => void;
  onOpen: () => void;
  onClose: () => void;
  onBuildSchedule?: () => void;
};

export default function CoachTimePollDetail({ poll, working, onEdit, onOpen, onClose, onBuildSchedule }: Props) {
  return (
    <div className="space-y-5">
      <Card variant="default">
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-lg font-semibold text-text">{poll.title}</p>
              <span className="rounded-full border border-border bg-surface-elevated px-2.5 py-1 text-xs font-semibold text-text-muted">{poll.status.charAt(0) + poll.status.slice(1).toLowerCase()}</span>
            </div>
            <p className="mt-1 text-sm text-text-muted">{poll.programName} · {poll.respondedCount} of {poll.athleteCount} families replied</p>
            {poll.deadlineAt ? <p className="mt-1 text-xs text-text-muted">Requested by {new Date(poll.deadlineAt).toLocaleString()}</p> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {poll.canEdit ? <Button variant="outline" leftIcon={<SquarePen />} onClick={onEdit}>Edit</Button> : null}
            {poll.canOpen ? <Button leftIcon={<Send />} isLoading={working} onClick={onOpen}>Ask families</Button> : null}
            {poll.canClose ? <Button variant="outline" isLoading={working} onClick={onClose}>Close poll</Button> : null}
            {poll.status === 'CLOSED' && onBuildSchedule ? <Button leftIcon={<CalendarPlus />} isLoading={working} onClick={onBuildSchedule}>Build draft schedule</Button> : null}
          </div>
        </CardContent>
      </Card>

      <Card variant="default">
        <CardHeader><CardTitle>Ranked options</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          {poll.slots.map((slot) => (
            <div key={slot.slotId} className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-medium text-text">{formatTimePollSlot(slot.startsAt, slot.endsAt)}</p>
                {slot.locationLabel ? <p className="mt-1 text-sm text-text-muted">{slot.locationLabel}</p> : null}
              </div>
              <div className="flex gap-2 text-xs font-semibold text-text-muted">
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-primary">1st {slot.firstChoiceCount}</span>
                <span className="rounded-full bg-surface-elevated px-2.5 py-1">2nd {slot.secondChoiceCount}</span>
                <span className="rounded-full bg-surface-elevated px-2.5 py-1">3rd {slot.thirdChoiceCount}</span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card variant="default">
        <CardHeader><CardTitle>Family responses</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          {poll.athletes.map((athlete) => (
            <div key={athlete.athleteId} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
              <p className="text-sm font-medium text-text">{athlete.athleteName}</p>
              <p className="text-sm text-text-muted">{athlete.responded ? (athlete.noneOfThese ? 'None work' : `${athlete.choices.length} ranked`) : 'Waiting'}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
